/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Link } from "react-router-dom";
import { BreadcrumbComponentProps } from "./breadcrumb-component-props";
import Logo from "../../../assets/icons/breadcrumb-logo.svg";
import ProfileSidebar from "../../../features/profile/profile-sidebar";
import avatorIcon from "../../../assets/icons/avatar.svg";
import { useSelector } from "react-redux";
import "./breadcrumb-component.scss";
import { useLogin } from "../../hooks/useUser";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { Box, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import notificationsIcon from "../../../assets/icons/notificationsIcon.svg";
import notificationsUnreadIcon from "../../../assets/icons/notificationsUnreadIcon.svg";
import { useSocket } from "../../hooks/useSocket";
import { getNotifications } from "../../../api/notifications";
import NotificationsPopUp from "../../../features/notifications/notifications-pop-up";
import { routes } from "../../../constants/routes";
import { useSetLoading } from "../../hooks/appearance";

const BreadcrumbComponent: React.FC<BreadcrumbComponentProps> = ({
  breadcrumbLink,
  showBackArrow = false,
  handleBackArrowClick,
}) => {
  const navigate = useNavigate();
  const tenantDetails = useLogin();
  const tenantLogoUrl = tenantDetails?.tenantDetails?.thumbnailUrlTwo;
  const socket = useSocket(
    tenantDetails?.userDetails?.id,
    tenantDetails?.tenantDetails?.id,
  );
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  const [hasUnreadNotification, setHasUnreadNotification] = useState(false);
  const [notificationsPopupOpen, setNotificationsPopupOpen] = useState(false);
  const [readNotifications, setReadNotifications] = useState([]);
  const [isProfileSidebarOpen, setIsProfileSidebarOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const isSuperParter = useMemo(() => {
    return tenantDetails?.tenantDetails?.tenantType === "SuperTenant";
  }, [tenantDetails?.tenantDetails?.tenantType]);
  const profileUrl = useSelector(
    (state: any) => state?.session?.userDetails?.profile_url
  );
    const handleProfileClick = (e?: React.MouseEvent | React.KeyboardEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      navigate(routes.PROFILE);
    };
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await getNotifications({});
      const notifications = res.data?.data || [];
      const unReadNotifications = notifications.filter((n: any) => !n.is_read);
      const readNotifications = notifications.filter((n: any) => n.is_read);
      setUnreadNotifications(unReadNotifications);
      setReadNotifications(readNotifications);
      if (unReadNotifications?.length > 0) {
        setHasUnreadNotification(true);
      } else {
        setHasUnreadNotification(false);
      }
    } catch (error) {
      console.error("Failed to fetch unread notifications", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchNotifications();
  }, [notificationsPopupOpen]);
  useEffect(() => {
    if (!socket) return;
    socket.on("notification", (notification) => {
      // Handle notification event
      console.log("Received notification:", notification);
      setHasUnreadNotification(true);
      fetchNotifications();
    });

    return () => {
      socket.off("notification");
    };
  }, [socket]);
  const handleNotificationClick = () => {
    setNotificationsPopupOpen(true);
  };
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);
  return (
    <>
      <div
        className={`breadcrumb-component ${
          tenantDetails?.gracePeriod
            ? "grace-period-alert-breadcrumb-component"
            : ""
        }`}
      >
        {tenantDetails?.gracePeriod && (
          <Box className="grace-period-alert">
            <ReportProblemIcon />
            <p>
              Your plan is in the grace period — renew or buy a new plan to
              avoid service interruption.
            </p>
          </Box>
        )}
        <Box
          className={`breadcrumb-wrapper ${
            tenantDetails?.gracePeriod ? "grace-period-alert-breadcrumb" : ""
          }`}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {showBackArrow && (
              <IconButton
                onClick={() => {
                  if (handleBackArrowClick) {
                    handleBackArrowClick();
                  } else {
                    navigate(-1);
                  }
                }}
                aria-label="Go Back"
              >
                <ArrowBackOutlinedIcon />
              </IconButton>
            )}

            <Stack spacing={2}>
              <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
              >
                {breadcrumbLink?.map((linkData: any, index: any) => {
                  const isLast = index === breadcrumbLink.length - 1;

                  if (isLast) {
                    return (
                      <Typography key={index} sx={{ color: "text.primary" }}>
                        {linkData?.label}
                      </Typography>
                    );
                  }

                  if (linkData?.handleClick) {
                    return (
                      <span
                        key={index}
                        className="breadcrumb-link"
                        onClick={linkData.handleClick}
                      >
                        {linkData.label}
                      </span>
                    );
                  }
                  if (linkData?.link) {
                    return (
                      <Link
                        key={index}
                        to={linkData.link}
                        className="breadcrumb-link"
                      >
                        {linkData.label}
                      </Link>
                    );
                  }

                  return (
                    <Typography key={index} sx={{ color: "text.secondary" }}>
                      {linkData.label}
                    </Typography>
                  );
                })}
              </Breadcrumbs>
            </Stack>
          </div>
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <img
              src={
                hasUnreadNotification
                  ? notificationsUnreadIcon
                  : notificationsIcon
              }
              className="notifications-icon"
              alt="notification"
              style={{
                cursor: "pointer",
                marginRight: "20px",
                marginBottom: "8px",
              }}
              onClick={handleNotificationClick}
            />
            <img
              src={profileUrl || avatorIcon}
              alt="Profile"
              className="breadcrumb-profile-avatar"
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                marginRight: "20px",
                cursor: "pointer",
                objectFit: "cover",
                marginBottom: "8px",
                border: "2px solid #e0e0e0"
              }}
              onClick={handleProfileClick}
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') handleProfileClick(e);
              }}
            />
            <img src={tenantLogoUrl ? tenantLogoUrl : Logo} alt="logo" />
          </div>
              {isProfileSidebarOpen && !isSuperParter && (
                <ProfileSidebar
                  isOpen={isProfileSidebarOpen}
                  onClose={() => setIsProfileSidebarOpen(false)}
                  onProfileClick={() => setIsProfileSidebarOpen(false)}
                />
              )}
        </Box>
      </div>
      {notificationsPopupOpen && (
        <div>
          <NotificationsPopUp
            setNotificationsPopupOpen={setNotificationsPopupOpen}
            setHasUnreadNotification={setHasUnreadNotification}
            readNotifications={readNotifications}
            unreadNotifications={unreadNotifications}
            fetchNotifications={fetchNotifications}
            setLoading={setLoading}
          />
        </div>
      )}
    </>
  );
};

export default BreadcrumbComponent;
